import { useState, useEffect, useCallback } from 'react';
import { PaymentMethod } from '@stripe/stripe-js';
import { isUserFromAustralia } from 'src/lib/utils';
import { useDecision } from '@optimizely/react-sdk';
import { isCardPaymentMethod } from '../utils/challengeAddon.util';

interface UsePaymentMethodStateProps {
  initialPaymentMethod?: PaymentMethod;
  onPaymentTypeChange?: (type: 'card' | 'afterpay') => void;
  shouldNotifyParent?: boolean;
}

interface UsePaymentMethodStateReturn {
  selectedPaymentType: 'card' | 'afterpay';
  paymentPopupVisible: boolean;
  setPaymentPopupVisible: (visible: boolean) => void;
  handlePaymentTypeChange: (type: 'card' | 'afterpay') => void;
  handlePaymentMethodUpdate: (
    newPaymentMethod: PaymentMethod,
    setPaymentMethod: (method: PaymentMethod) => void
  ) => void;
  handleAddCardClick: () => void;
}

export const usePaymentMethodState = ({
  initialPaymentMethod,
  onPaymentTypeChange,
  shouldNotifyParent = true,
}: UsePaymentMethodStateProps): UsePaymentMethodStateReturn => {
  const [{ enabled: isAfterPayDisabled }] = useDecision(
    'disable_afterpay_all_flow',
    {
      autoUpdate: true,
    }
  );

  const hasCard = isCardPaymentMethod(initialPaymentMethod);

  const defaultPaymentMethod =
    isUserFromAustralia() && !isAfterPayDisabled
      ? hasCard
        ? 'card'
        : 'afterpay'
      : 'card';

  const [selectedPaymentType, setSelectedPaymentType] = useState<
    'card' | 'afterpay'
  >(defaultPaymentMethod);
  const [paymentPopupVisible, setPaymentPopupVisible] = useState(false);

  useEffect(() => {
    if (shouldNotifyParent && onPaymentTypeChange) {
      onPaymentTypeChange(selectedPaymentType);
    }
  }, [selectedPaymentType, onPaymentTypeChange, shouldNotifyParent]);

  const handlePaymentTypeChange = useCallback((type: 'card' | 'afterpay') => {
    setSelectedPaymentType(type);
  }, []);

  const handleAddCardClick = useCallback(() => {
    setPaymentPopupVisible(true);
  }, []);

  const handlePaymentMethodUpdate = useCallback(
    (
      newPaymentMethod: PaymentMethod,
      setPaymentMethod: (method: PaymentMethod) => void
    ) => {
      setPaymentMethod(newPaymentMethod);
      setSelectedPaymentType('card');
      setPaymentPopupVisible(false);
    },
    []
  );

  return {
    selectedPaymentType,
    paymentPopupVisible,
    setPaymentPopupVisible,
    handlePaymentTypeChange,
    handlePaymentMethodUpdate,
    handleAddCardClick,
  };
};
